import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import type { FilterOptionsState, SxProps, Theme } from "@mui/material";
import { Autocomplete, Box, InputAdornment, Paper, Popper, Skeleton, TextField, Tooltip as MuiTooltip } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Chip from "../chip/Chip";
import InformationTooltip from "../informationTooltip/InformationTooltip";
import { isNullOrWhitespace } from "../stringOperations";
import Tooltip from "../tooltip/Tooltip";
import type { RightIcons } from "./iconsContainer";
import type { inputVariant } from "./inputStyles";
import { useMultiSelectStyles } from "./MultiSelect.styles";
import type { SelectOption } from "./MultiSelect.types";

interface MultiSelectBaseProps {
    customFilterOptions?: (options: SelectOption[], state: FilterOptionsState<SelectOption>) => SelectOption[];
    options: Array<SelectOption> | undefined;
    chosenOptions: Array<string>;
    selectedValueText?: string;
    placeholder?: string;
    label: string;
    disabled?: boolean;
    disableSelection?: boolean;
    disableClearable?: boolean;
    disableItemRemoval?: boolean;
    disableSearch?: boolean;
    isLoading?: boolean;
    helperText?: string;
    errorText?: string | JSX.Element;
    singleSelect?: boolean;
    noMargin?: boolean;
    limit?: number;
    children?: any;
    searchValue?: string;
    paperClassName?: string;
    paperSx?: SxProps<Theme>;
    showHoverOnInputField?: boolean;
    renderOptions?: (option: SelectOption) => JSX.Element;
    width?: string;
    variant?: inputVariant;
    noOptionsLabel: string;
    backgroundColor?: "white" | "transparent";
    icon?: JSX.Element;
    inputStartAdornment?: JSX.Element;
    clearOnSelect?: boolean;
    alwaysOpenInBottom?: boolean;
    focusAndOpen?: boolean;
    listMaxHeight?: number;
    showPopupIconWhenDisabled?: boolean;
    onClose?: () => void;
    onItemAdded?: (item: string) => void;
    onItemDeleted?: (item: string) => void;
    onSearchValueChange?: (value: any) => void;
    onFocus?: () => void;
    onBlur?: () => void;
    onChipClick?: (optionId: string) => void;
    onChange: (values: Array<string>) => void;
}

export type MultiSelectProps = MultiSelectBaseProps & RightIcons;

function getInputAndSelectionDisabled(props: MultiSelectProps) {
    return props.disabled || props.disableSelection;
}

const getRenderInput = (
    inputProps: React.InputHTMLAttributes<HTMLInputElement>,
    rest: any,
    chosenProps: Array<SelectOption>,
    props: MultiSelectProps,
    inputRef: React.RefObject<HTMLInputElement>,
    helperTextStyle: SxProps<Theme>
) => {
    const textField = getTextField(inputProps, rest, props, inputRef, helperTextStyle);
    return <MuiTooltip title={props.showHoverOnInputField ? formatTooltipText(props.singleSelect, chosenProps) : ""}>{textField}</MuiTooltip>;
};

const getTextField = (
    inputProps: React.InputHTMLAttributes<HTMLInputElement>,
    rest: any,
    props: MultiSelectProps,
    inputRef: React.RefObject<HTMLInputElement>,
    helperTextStyle: any
) => {
    let InputProps = { ...rest.InputProps };
    if (props.inputStartAdornment && props.singleSelect) {
        InputProps = {
            ...rest.InputProps,
            startAdornment: (
                <InputAdornment position="start" sx={{ marginLeft: "12px" }}>
                    {props.inputStartAdornment}
                </InputAdornment>
            ),
        };
    }

    return (
        <TextField
            {...rest}
            inputRef={inputRef}
            sx={[
                (props.errorText !== null && props.errorText !== undefined) || !isNullOrWhitespace(props.errorText as string | undefined)
                    ? undefined
                    : helperTextStyle,
                {
                    "& .MuiInputBase-root:hover": { cursor: getInputAndSelectionDisabled(props) ? "default" : props.disableSearch ? "pointer" : "inherit" },
                    "& input:hover": { cursor: getInputAndSelectionDisabled(props) ? "default" : props.disableSearch ? "pointer" : "inherit" },
                },
            ]}
            variant={props.variant ?? "outlined"}
            label={props.label}
            InputLabelProps={{
                shrink: props.variant === "standard" ? true : undefined,
            }}
            placeholder={props.chosenOptions ? (props.chosenOptions.length === 0 ? props.placeholder : undefined) : undefined}
            error={
                (props.errorText !== null && props.errorText !== undefined && !!props.errorText) || !isNullOrWhitespace(props.errorText as string | undefined)
            }
            helperText={
                (props.errorText !== null && props.errorText !== undefined) || !isNullOrWhitespace(props.errorText as string | undefined)
                    ? props.errorText
                    : props.helperText
            }
            InputProps={InputProps}
            inputProps={{ ...inputProps, readOnly: props.disableSearch }}
            disabled={getInputAndSelectionDisabled(props)}
        />
    );
};

const PopperMy = function (props: any) {
    const modifiers = [
        {
            name: "flip",
            options: {
                fallbackPlacements: [],
            },
        },
    ];
    return <Popper {...props} placement="bottom-start" modifiers={modifiers} />;
};

function MultiSelect(props: MultiSelectProps) {
    const styles = useMultiSelectStyles(props);
    const chosenProps: Array<SelectOption> = [];
    const inputRef = useRef<HTMLInputElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (props.focusAndOpen) {
            inputRef.current!.focus();
            setIsOpen(true);
        }
    }, [props.focusAndOpen]);

    if (!props.isLoading && props.options && props.chosenOptions) {
        props.chosenOptions.forEach((value) => {
            const option = props.options!.find((x) => x.id === value);
            if (option) {
                chosenProps.push(option);
            }
        });
    }

    const handleOnSearchValueChange = (e: any) => {
        if (props.onSearchValueChange && e !== null) {
            if (e.type === "change") {
                props.onSearchValueChange(e.target.value);
            } else {
                props.onSearchValueChange("");
            }
        }
    };

    const handleInputValue = () => {
        if (props.singleSelect && !isNullOrWhitespace(props.searchValue)) {
            return props.searchValue;
        } else if (props.singleSelect && chosenProps[0]) {
            return chosenProps[0].name;
        }
        return props.searchValue;
    };

    const getConditionalProps = () => {
        let searchValueChange = {};
        if (props.onSearchValueChange) {
            searchValueChange = {
                onInputChange: (e: any) => handleOnSearchValueChange(e),
                inputValue: handleInputValue(),
            };
        }
        return { ...searchValueChange };
    };

    const getLiComponent = (renderProps: any, option: SelectOption, key?: React.Key) => {
        return (
            <Box
                component={"li"}
                {...renderProps}
                key={key}
                sx={{ backgroundColor: "unset", "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" }, display: "flex", alignItems: "center", gap: 1 }}
            >
                {props.renderOptions ? props.renderOptions(option) : option.name}
                {!option.disabled && option.hoverText && <InformationTooltip text={option.hoverText} />}
            </Box>
        );
    };

    const getRenderOption = (renderProps: any, option: SelectOption) =>
        option.disabled && option.hoverText ? (
            <MuiTooltip key={option.id} title={option.hoverText as React.ReactNode} placement="bottom-start">
                <span>{getLiComponent(renderProps, option)}</span>
            </MuiTooltip>
        ) : (
            getLiComponent(renderProps, option, option.id)
        );

    const autocomplete = (
        <Autocomplete
            selectOnFocus={!props.disableSearch}
            ListboxProps={{ sx: { maxHeight: props.listMaxHeight ? props.listMaxHeight : "auto", fontSize: "0.875rem" } }}
            blurOnSelect={props.clearOnSelect}
            disabled={getInputAndSelectionDisabled(props)}
            limitTags={props.limit ?? -1}
            sx={styles.root}
            size="small"
            open={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={() => {
                setIsOpen(false);
                if (props.onClose) {
                    props.onClose();
                }
            }}
            onBlur={props.onBlur}
            onFocus={props.onFocus}
            PopperComponent={props.alwaysOpenInBottom ? PopperMy : undefined}
            noOptionsText={props.noOptionsLabel}
            multiple={!props.singleSelect}
            options={props.options ? props.options : []}
            getOptionLabel={(option: SelectOption) => (option ? option.name : "")}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={props.singleSelect ? (chosenProps[0] ? chosenProps[0] : null) : chosenProps}
            onChange={(_event, value) => handleChange(value)}
            filterSelectedOptions
            filterOptions={props.customFilterOptions}
            disableClearable={props.disableClearable || props.disableItemRemoval}
            popupIcon={props.disabled && !props.showPopupIconWhenDisabled ? null : props.icon ? props.icon : <KeyboardArrowDownIcon />}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                        <Box
                            key={option.id}
                            sx={{
                                ...styles.chipContainer,
                                "& .MuiChip-deleteIcon": {
                                    display: option.unDeletable ? "none" : "block",
                                },
                            }}
                        >
                            <Chip
                                key={key}
                                {...tagProps}
                                onDelete={props.disableItemRemoval ? undefined : tagProps.onDelete}
                                value={option.name}
                                color={option.color}
                                fontStyle={option.fontStyle}
                                deletionIcon={<ClearIcon sx={{ width: 16, height: 16 }} />}
                                hoverText={option.hoverText}
                                size="small"
                                isAutocomplete
                                disabled={props.disabled}
                                onClick={props.onChipClick !== undefined ? () => props.onChipClick!(option.id) : undefined}
                            />
                        </Box>
                    );
                })
            }
            {...getConditionalProps()}
            renderOption={getRenderOption}
            renderInput={({ inputProps, ...rest }) => getRenderInput(inputProps, rest, chosenProps, props, inputRef, styles.helperText)}
            PaperComponent={({ children, ...other }) => {
                return (
                    <Paper
                        {...other}
                        className={props.paperClassName}
                        sx={[{ "& .MuiAutocomplete-noOptions": { fontSize: 14 } }, ...(Array.isArray(props.paperSx) ? props.paperSx : [props.paperSx])]}
                    >
                        {children}
                        {props.children}
                    </Paper>
                );
            }}
            getOptionDisabled={(option) => option.disabled ?? false}
        />
    );

    function handleChange(value: SelectOption | Array<SelectOption> | null) {
        if (value) {
            if (props.singleSelect) {
                const singleValue = value as SelectOption;
                props.onChange([singleValue.id]);
            } else {
                const multiValue = value as Array<SelectOption>;
                const newIds = multiValue.map((v) => v.id);
                props.onChange(newIds);

                if (props.onItemAdded) {
                    const addedItems = newIds.filter((id) => !props.chosenOptions.includes(id));
                    if (addedItems.length === 1) {
                        props.onItemAdded(addedItems[0]);
                    }
                }

                if (props.onItemDeleted) {
                    const deletedItems = props.chosenOptions.filter((id) => !newIds.includes(id));
                    if (deletedItems.length === 1) {
                        props.onItemDeleted(deletedItems[0]);
                    }
                }
            }
        } else {
            props.onChange([]);
        }
    }

    return (
        <Box sx={styles.container}>
            {props.isLoading ? (
                <Skeleton sx={{ borderRadius: "10px" }} variant="rectangular" width={"100%"}>
                    {autocomplete}
                </Skeleton>
            ) : (
                <React.Fragment>{props.selectedValueText ? <Tooltip text={props.selectedValueText}>{autocomplete}</Tooltip> : autocomplete}</React.Fragment>
            )}
            <Box sx={styles.iconsContainer}>
                {props.warningText && (
                    <Tooltip text={props.warningText}>
                        <PriorityHighIcon sx={styles.exclamationIcon} />
                    </Tooltip>
                )}
                {props.toolTipText && <InformationTooltip text={props.toolTipText} />}
                {props.additionalIcon && (
                    <Tooltip text={props.additionalIcon.tooltipText}>
                        <div>{props.additionalIcon.icon}</div>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
}

function formatTooltipText(isSingleSelect: boolean | undefined, chosenOptions: Array<SelectOption>) {
    let tooltip = "";
    if (isSingleSelect) tooltip = chosenOptions[0] ? chosenOptions[0].name : "";
    else {
        tooltip = chosenOptions.map((element) => element.name).join(", ");
    }
    return tooltip;
}

export default MultiSelect;
